import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { OnboardingService } from '../../../services/onboarding/onboarding.service';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { FieldValidators } from '../../shared/validators/field-validators';
import { FieldErrorComponent } from '../../shared/components/field-error/field-error.component';
import { isFieldInvalid,validateFile } from '../../shared/validators/form-validation.utils';
import { LinkdindetailsFetchComponent } from '../../../student-portal/application-form/sections/linkdindetails-fetch/linkdindetails-fetch.component';

export interface DocFileItem {
  file?: File;
  previewUrl: string;
  name: string;
  type: string;
  base64Data?: string;
  isExistingUrl: boolean;
}


export interface DocumentParseData {
  jobId: string;
  status: 'pending' | 'parsing' | 'completed' | 'error';
  extractedData?: any;
  pollCount?: number;
}

@Component({
  selector: 'app-da-test-scores',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectButtonModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    FieldErrorComponent,
    LinkdindetailsFetchComponent
  ],
  providers: [MessageService],
  templateUrl: './test-scores.component.html',
  styleUrl: './test-scores.component.scss'
})
export class DaTestScoresComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;

  @ViewChild(LinkdindetailsFetchComponent) docReadingModal!: LinkdindetailsFetchComponent;
  isDocModalVisible: boolean = false;
  docModalFileName: string = '';
  activeDocModalField: string = '';
  pendingExtractedData: any = null;
  parseJobData: { [field: string]: DocumentParseData } = {};
  private pollTimeouts: any[] = [];
@Output() loaded = new EventEmitter<void>();
  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  displayPreviewDialog: boolean = false;
  safePdfUrl: SafeResourceUrl | null = null;
  previewFileName: string = '';
  originalFileUrl: string = '';
  isPdfLoading: boolean = false;
  isDataExists: boolean = false;
  isSaving: boolean = false;
  existingRecordId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private sanitizer: DomSanitizer,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    if (!this.parentForm.get('hasElpTest')) {
      this.parentForm.addControl('hasElpTest', this.fb.control(null, Validators.required));
    }
    if (!this.parentForm.get('testScores')) {
      this.parentForm.addControl('testScores', this.fb.array([]));
    }

    // React to Yes/No toggle — add or remove the scorecard group
    this.parentForm.get('hasElpTest')?.valueChanges.subscribe((val: boolean) => {
      if (val) {
        this.testScores.enable();
        if (this.testScores.length === 0) {
          this.testScores.push(this.createBlankScoreGroup());
        }
      } else {
        this.testScores.disable();
        this.testScores.clear(); 
      }
      // Do NOT markAsPristine here — we need dirty tracked so Save button warning works
    });

    this.loadTestScoresData();
  }

  get testScores(): FormArray {
    return this.parentForm.get('testScores') as FormArray;
  }

  // ── Build a DocFileItem from an existing blob/SAS URL ──────────────────
  private buildFileItem(url: string): DocFileItem {
    const urlWithoutParams = url.split('?')[0];
    const name = urlWithoutParams.split('/').pop() || 'document';
    const lower = urlWithoutParams.toLowerCase();
    let type = 'application/octet-stream';
    if (lower.endsWith('.pdf')) {
      type = 'application/pdf';
    } else if (['.jpg', '.jpeg', '.png', '.webp'].some(ext => lower.endsWith(ext))) {
      type = 'image/jpeg';
    }
    return { previewUrl: url, name, type, isExistingUrl: true };
  }

  // ── Create a blank scorecard FormGroup (for new entries) ───────────────
  private createBlankScoreGroup(): ReturnType<FormBuilder['group']> {
    return this.fb.group({
      id: [null],
      testName: ['SCORECARD'],
      ieltsScore: ['', FieldValidators.testScore],
      toeflScore: ['', FieldValidators.testScore],
      duolingoScore: ['', FieldValidators.testScore],
      apExamScore: ['', FieldValidators.testScore],
      satScore: ['', FieldValidators.testScore],
      greTotalScore: ['', FieldValidators.testScore],
      gmatScore: ['', FieldValidators.testScore],
      actScore: ['', FieldValidators.testScore],
      ieltsDoc: [null],
      toeflDoc: [null],
      duolingoDoc: [null],
      apDoc: [null],
      satDoc: [null],
      greDoc: [null],
      gmatDoc: [null],
      actDoc: [null],
    }, { validators: this.testScoreValidator() });
  }

  // ── Load from API and patch FormArray ──────────────────────────────────
  loadTestScoresData() {
    try {
      const raw = localStorage.getItem('account');
      if (!raw) return;
      const account = JSON.parse(raw);
      const userId = account?.userId;
      if (!userId) return;

      this.onboardingService.getTestScores(userId).subscribe({
        next: (res: any) => {
          const data = Array.isArray(res) ? res[0] : (res?.result?.[0] || res);
         if (data && (data.id || data.Id)) {
  this.existingRecordId = data.id || data.Id;
  this.isDataExists = true;
  const isEngNotGiven = data.isEnglishProficiencyTestNotGiven ?? false;
  const hasElp = !isEngNotGiven;
  this.parentForm.get('hasElpTest')?.setValue(hasElp);

  this.testScores.clear();

  if (hasElp) {
    // sirf tab group push karo jab user ne actually ELP test diya ho
    const group = this.fb.group({
      id: [data.id || data.Id || null],
      testName: ['SCORECARD'],
      ieltsScore: [data.ieltsScores?.score || '', FieldValidators.testScore],
      toeflScore: [data.toeflScores?.score || '', FieldValidators.testScore],
      duolingoScore: [data.duolingoScores?.score || '', FieldValidators.testScore],
      apExamScore: [data.apExams?.[0]?.subjectScore || '', FieldValidators.testScore],
      satScore: [data.satScores?.score || '', FieldValidators.testScore],
      greTotalScore: [data.greScores?.score || '', FieldValidators.testScore],
      gmatScore: [data.gmatScores?.score || '', FieldValidators.testScore],
      actScore: [data.actScores?.score || '', FieldValidators.testScore],
      ieltsDoc: [data.ieltsScores?.documentUrl ? this.buildFileItem(data.ieltsScores.documentUrl) : null],
      toeflDoc: [data.toeflScores?.documentUrl ? this.buildFileItem(data.toeflScores.documentUrl) : null],
      duolingoDoc: [data.duolingoScores?.documentUrl ? this.buildFileItem(data.duolingoScores.documentUrl) : null],
      apDoc: [data.apExams?.[0]?.documentUrl ? this.buildFileItem(data.apExams[0].documentUrl) : null],
      satDoc: [data.satScores?.documentUrl ? this.buildFileItem(data.satScores.documentUrl) : null],
      greDoc: [data.greScores?.documentUrl ? this.buildFileItem(data.greScores.documentUrl) : null],
      gmatDoc: [data.gmatScores?.documentUrl ? this.buildFileItem(data.gmatScores.documentUrl) : null],
      actDoc: [data.actScores?.documentUrl ? this.buildFileItem(data.actScores.documentUrl) : null],
    }, { validators: this.testScoreValidator() });
    this.testScores.push(group);
  }
  // hasElp false hone par testScores array khaali hi rahega — koi group push nahi

  this.parentForm.get('hasElpTest')?.markAsPristine();
  this.testScores.markAsPristine();
} else {
  this.parentForm.get('hasElpTest')?.setValue(false);
  this.parentForm.get('hasElpTest')?.markAsPristine();
  this.testScores.clear();
}
            this.loaded.emit();
        },
        error: (err) => {
          console.warn('Failed to load test scores:', err);
          this.loaded.emit();
        }
      });
    } catch (e) {
      console.error('Error in loadTestScoresData:', e);
      this.loaded.emit();
    }
  }

  // ── Read doc value from a specific FormArray row ───────────────────────
  getDocValue(index: number, field: string): DocFileItem | null {
    return this.testScores.at(index)?.get(field)?.value || null;
  }

  // ── Open existing URL in preview dialog ────────────────────────────────
  openFile(docVal: DocFileItem | null): void {
    if (!docVal || !docVal.previewUrl) return;
    const url = docVal.previewUrl;
    this.originalFileUrl = url;
    this.previewFileName = docVal.name || 'Document Preview';
    this.isPdfLoading = true;
    // Local files (data: or blob: URLs) must be opened directly;
    // Remote server URLs are routed through Google Docs Viewer
    let viewerUrl: string;
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      viewerUrl = url;
    } else {
      viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    }
    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    this.displayPreviewDialog = true;
  }

  // ── Handle file input change for any doc field ─────────────────────────
  onFileSelectedForTest(event: Event, field: string, inputRef: HTMLInputElement): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];

    const check = validateFile(file);
    if (!check.valid) {
      this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
      inputRef.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const item: DocFileItem = {
        file,
        previewUrl: e.target.result,
        name: file.name,
        type: file.type,
        base64Data: e.target.result,
        isExistingUrl: false
      };
      const control = this.testScores.at(0)?.get(field);
      if (control) {
        control.setValue(item);
        control.markAsDirty();
      }
    };
    reader.readAsDataURL(file);
    inputRef.value = '';

    if (!this.parseJobData[field]) {
      this.parseJobData[field] = { jobId: '', status: 'pending' };
    }
    this.processFileAndParse(file, field);
  }

  // ==================== DOCUMENT PARSING FLOW ====================

  processFileAndParse(file: File, field: string) {
    this.submitDocumentForParsing(file, field);
  }

  private submitDocumentForParsing(file: File, field: string) {
    this.docModalFileName = file.name;
    this.activeDocModalField = field;
    this.isDocModalVisible = true;

    setTimeout(() => {
      this.docReadingModal?.show(file.name);
    }, 0);

    const docType = 'SCORECARD';
    this.onboardingService.parseDocument(file, docType).subscribe({
      next: (response: any) => {
        const jobId = response.job_id;
        this.parseJobData[field] = { jobId, status: 'parsing', pollCount: 0 };
        this.startPollingForResult(jobId, field);
      },
      error: (err: any) => {
        console.error('Failed to submit document for parsing:', err);
        this.docReadingModal?.showError();
        if (this.parseJobData[field]) this.parseJobData[field].status = 'error';
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to submit document for analysis', life: 3000 });
      }
    });
  }

  private startPollingForResult(jobId: string, field: string) {
    const maxPolls = 30;
    this.parseJobData[field].pollCount = 0;

    const poll = () => {
      this.parseJobData[field].pollCount = (this.parseJobData[field].pollCount || 0) + 1;
      this.onboardingService.getDocumentParseResult(jobId).subscribe({
        next: (response: any) => {
          const status = (response.status || '').toLowerCase();
          if ((status === 'completed' || status === 'done') && response.result) {
            this.parseJobData[field].status = 'completed';
            this.parseJobData[field].extractedData = response.result;
            this.pendingExtractedData = response.result;
            if (this.activeDocModalField === field) { this.docReadingModal?.completeNow(); }
          } else if (status === 'failed' || status === 'error') {
            this.parseJobData[field].status = 'error';
            if (this.activeDocModalField === field) { this.docReadingModal?.showError(); }
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to analyze document', life: 3000 });
          } else {
            const pollCount = (this.parseJobData[field].pollCount || 0);
            if (pollCount < maxPolls) {
              const t = setTimeout(poll, 2000); this.pollTimeouts.push(t);
            } else {
              this.parseJobData[field].status = 'error';
              if (this.activeDocModalField === field) { this.docReadingModal?.showError(); }
              this.messageService.add({ severity: 'warn', summary: 'Timeout', detail: 'Document analysis took too long.', life: 3000 });
            }
          }
        },
        error: () => {
          const pollCount = (this.parseJobData[field].pollCount || 0);
          if (pollCount < maxPolls) { const t = setTimeout(poll, 2000); this.pollTimeouts.push(t); }
          else { this.parseJobData[field].status = 'error'; if (this.activeDocModalField === field) { this.docReadingModal?.showError(); } }
        }
      });
    };
    const t = setTimeout(poll, 1000);
    this.pollTimeouts.push(t);
  }

  onDocModalComplete(): void {
    this.isDocModalVisible = false;
    if (this.activeDocModalField && this.pendingExtractedData) {
      this.autoFillTestScoreFromParsedData(this.activeDocModalField, this.pendingExtractedData);
    }
    this.pendingExtractedData = null;
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document analyzed! Fields have been auto-filled.', life: 3000 });
  }

  onDocModalCancel(): void {
    this.pendingExtractedData = null;
    this.isDocModalVisible = false;
  }

  onDocModalClose(): void {
    this.isDocModalVisible = false;
  }

private autoFillTestScoreFromParsedData(field: string, extractedData: any) {
  const tsGroup = this.testScores.at(0) as FormGroup;
  if (!tsGroup) return;

  // Helper: score extract karo extracted data se
  const getScore = (obj: any, ...keys: string[]): any => {
    for (const key of keys) {
      if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
    }
    return null;
  };

  // scoreField aur extraction logic ka mapping
  const fieldMap: { [key: string]: { formField: string; extract: () => any } } = {
    ieltsDoc: {
      formField: 'ieltsScore',
      extract: () => {
        const obj = extractedData.ielts_score || extractedData.ielts_scores || extractedData.ieltsScores || extractedData;
        return getScore(obj, 'score', 'overall_band_score', 'overall');
      }
    },
    toeflDoc: {
      formField: 'toeflScore',
      extract: () => {
        const obj = extractedData.toefl_score || extractedData.toefl_scores || extractedData.toeflScores || extractedData;
        return getScore(obj, 'score', 'total_score', 'total');
      }
    },
    duolingoDoc: {
      formField: 'duolingoScore',
      extract: () => {
        const obj = extractedData.duolingo_score || extractedData.duolingo_scores || extractedData.duolingoScores || extractedData;
        return getScore(obj, 'score', 'overall_score', 'overall');
      }
    },
    apDoc: {
      formField: 'apExamScore',
      extract: () => {
        const obj = extractedData.ap_score || extractedData.ap_scores || extractedData.apExams || extractedData;
        return getScore(obj, 'score', 'subject_score', 'overall');
      }
    },
    satDoc: {
      formField: 'satScore',
      extract: () => {
        const obj = extractedData.sat_score || extractedData.sat_scores || extractedData.satScores || extractedData;
        return getScore(obj, 'score', 'total_score', 'total');
      }
    },
    greDoc: {
      formField: 'greTotalScore',
      extract: () => {
        const obj = extractedData.gre_score || extractedData.gre_scores || extractedData.greScores || extractedData;
        return getScore(obj, 'score', 'total_score', 'total');
      }
    },
    gmatDoc: {
      formField: 'gmatScore',
      extract: () => {
        const obj = extractedData.gmat_score || extractedData.gmat_scores || extractedData.gmatScores || extractedData;
        return getScore(obj, 'score', 'total_score', 'total');
      }
    },
    actDoc: {
      formField: 'actScore',
      extract: () => {
        const obj = extractedData.act_score || extractedData.act_scores || extractedData.actScores || extractedData;
        return getScore(obj, 'score', 'composite_score', 'composite');
      }
    }
  };

  const mapping = fieldMap[field];
  if (!mapping) return;

  const scoreValue = mapping.extract();

  // API se aaya — overwrite karo
  // API se nahi aaya (null/undefined) — field untouched rahegi
  if (scoreValue !== null && scoreValue !== undefined) {
    tsGroup.get(mapping.formField)?.setValue(scoreValue);
    tsGroup.get(mapping.formField)?.markAsDirty();
  }

  tsGroup.markAsDirty();
}

  ngOnDestroy() {
    this.pollTimeouts.forEach(t => clearTimeout(t));
  }
  // ── Remove a doc ───────────────────────────────────────────────────────
  removeDoc(index: number, field: string): void {
    const control = this.testScores.at(index)?.get(field);
    if (control) {
      control.setValue(null);
      control.markAsDirty();
    }
  }

  // ── Legacy helper kept in case parent uses it ──────────────────────────
  getScoreFileName(testName: string | null | undefined): string {
    if (!testName) return 'Scorecard.pdf';
    const parts = testName.trim().split(' ');
    return `${parts[0] || 'Test'}_Scorecard.pdf`;
  }

  saveTestScores() {
    const tsGroup = this.testScores.length > 0 ? this.testScores.at(0) as FormGroup : null;
    const hasElpTestControl = this.parentForm.get('hasElpTest');

    if (tsGroup) {
      tsGroup.markAllAsTouched();
    }

    // Determine if anything was changed
    const tsGroupDirty = tsGroup ? tsGroup.dirty : false;
    const hasElpDirty = hasElpTestControl ? hasElpTestControl.dirty : false;

    if (!tsGroupDirty && !hasElpDirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      return;
    }

    if (tsGroup && tsGroup.invalid) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill in all required fields.' });
      this.scrollToFirstError();
      return;
    }

    let userId = '';
    try {
      const raw = localStorage.getItem('account');
      if (raw) {
        userId = JSON.parse(raw)?.userId || '';
      }
    } catch (e) { }

    if (!userId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'User ID not found.' });
      return;
    }

    const buildScoreObj = (score: string, docItem: DocFileItem | null) => {
      if (!score || score.toString().trim() === '') return null;
      let docUrl = null;
      if (docItem) {
        docUrl = docItem.base64Data || (docItem.isExistingUrl ? docItem.previewUrl : null);
      }
      return {
        score: parseFloat(score) || 0,
        documentUrl: docUrl
      };
    };

    let payload: any = {
      applicationUserId: userId
    };

    if (tsGroup) {
      const vals = tsGroup.value;
      const hasElp = hasElpTestControl?.value;
      const hasStdScore = !!vals.satScore || !!vals.greTotalScore || !!vals.gmatScore || !!vals.apExamScore || !!vals.actScore;

      payload = {
        ...payload,
        isStanardizedTestNotGiven: !hasStdScore,
        satScores: buildScoreObj(vals.satScore, vals.satDoc),
        actScores: buildScoreObj(vals.actScore, vals.actDoc),
        greScores: buildScoreObj(vals.greTotalScore, vals.greDoc),
        gmatScores: buildScoreObj(vals.gmatScore, vals.gmatDoc),
        isEnglishProficiencyTestNotGiven: !hasElp,
        toeflScores: buildScoreObj(vals.toeflScore, vals.toeflDoc),
        ieltsScores: buildScoreObj(vals.ieltsScore, vals.ieltsDoc),
        duolingoScores: buildScoreObj(vals.duolingoScore, vals.duolingoDoc),
        apExams: (vals.apExamScore && vals.apExamScore.toString().trim() !== '') ? [{
          subjectName: "AP",
          subjectScore: parseFloat(vals.apExamScore) || 0,
          documentUrl: vals.apDoc ? (vals.apDoc.base64Data || (vals.apDoc.isExistingUrl ? vals.apDoc.previewUrl : null)) : null
        }] : null
      };

      if (vals.id) {
        payload.id = vals.id;
      }
    } else {
      payload.isStanardizedTestNotGiven = true;
      payload.isEnglishProficiencyTestNotGiven = !hasElpTestControl?.value;
    }

    const existingId = tsGroup?.value?.id || this.existingRecordId;

    if (existingId) {
      payload.id = existingId;
      this.isSaving = true;
      this.onboardingService.updateTestScore(payload).subscribe({
        next: () => {
          this.isSaving = false;
          this.isDataExists = true;
          this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Test scores updated successfully.' });
          if (tsGroup) tsGroup.markAsPristine();
          hasElpTestControl?.markAsPristine();
        },
        error: () => {
          this.isSaving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update test scores.' });
        }
      });
    } else {
      this.isSaving = true;
      this.onboardingService.createTestScore(payload).subscribe({
        next: (res: any) => {
          this.isSaving = false;
          this.isDataExists = true;
          if (res?.id || res?.result?.id) {
            if (tsGroup) tsGroup.get('id')?.setValue(res?.id || res?.result?.id);
          }
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Test scores saved successfully.' });
          if (tsGroup) tsGroup.markAsPristine();
          hasElpTestControl?.markAsPristine();
        },
        error: () => {
  this.isSaving = false;
  this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update test scores.' });
}
      });
    }
  }

  readonly isFieldInvalid = isFieldInvalid;

  private scrollToFirstError() {
    setTimeout(() => {
      const selectors = [
        'input.ng-invalid',
        'textarea.ng-invalid',
        'p-dropdown.ng-invalid',
        'p-calendar.ng-invalid',
        'p-autocomplete.ng-invalid',
        'p-selectbutton.ng-invalid'
      ];
      const el = document.querySelector(selectors.join(', '));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el as HTMLElement;
        if (typeof focusable.focus === 'function') {
          focusable.focus();
        }
      }
    }, 100);
  }

  testScoreValidator() {
    return (group: AbstractControl) => {
      const pairs = [
        { s: 'ieltsScore', d: 'ieltsDoc' },
        { s: 'toeflScore', d: 'toeflDoc' },
        { s: 'duolingoScore', d: 'duolingoDoc' },
        { s: 'apExamScore', d: 'apDoc' },
        { s: 'satScore', d: 'satDoc' },
        { s: 'greTotalScore', d: 'greDoc' },
        { s: 'gmatScore', d: 'gmatDoc' },
        { s: 'actScore', d: 'actDoc' }
      ];
      let errors: any = {};
      let hasError = false;
      let hasAnyScore = false;

      pairs.forEach(pair => {
        const scoreVal = group.get(pair.s)?.value;
        const docVal = group.get(pair.d)?.value;
        const hasScore = !!scoreVal && scoreVal.toString().trim() !== '';
        const hasDoc = !!docVal;

        if (!hasScore && hasDoc) {
          errors['missingScore_' + pair.s] = true;
          hasError = true;
        }
        if (hasScore) {
          hasAnyScore = true;
        }
      });

      if (!hasAnyScore) {
        errors['noScore'] = true;
        hasError = true;
      }

      return hasError ? errors : null;
    };
  }
}